import { HttpClient, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ErrorService } from '@cosmos/angular/utils';
import { Invoice, InvoiceBillingSnapshot, InvoiceMetricSelection, InvoicePreviewResponse } from '@cosmos/shared';
import { catchError, Observable } from 'rxjs';
import { environment } from '../../../../../../environments/environment';

export interface AddLineItemBody {
  description: string;
  module: string;
  quantity: number;
  unitPrice: number;
  currency?: string;
  isDiscount?: boolean;
  linkedMetric?: string;
}

export interface UpdateLineItemBody {
  description?: string;
  unitPrice?: number;
  quantity?: number;
}

export interface UpdateDiscountsBody {
  discountFlat?: number;
  discountPercent?: number;
  notes?: string;
}

@Injectable({
  providedIn: 'root'
})
export class InvoicesService {

  constructor(
    private http: HttpClient,
    private errorService: ErrorService,
  ) {}

  find(query = ''): Observable<HttpResponse<Invoice[]>> {
    return this.http
      .get<Invoice[]>(`${environment.API_URL}/invoices${query}`, { observe: 'response' })
      .pipe(catchError(e => { this.errorService.handleError(e); throw e; }));
  }

  findById(id: string, query = ''): Observable<HttpResponse<Invoice>> {
    return this.http
      .get<Invoice>(`${environment.API_URL}/invoices/${id}${query}`, { observe: 'response' })
      .pipe(catchError(e => { this.errorService.handleError(e); throw e; }));
  }

  create(body: Partial<Invoice>): Observable<HttpResponse<Invoice>> {
    return this.http
      .post<Invoice>(`${environment.API_URL}/invoices`, body, { observe: 'response' })
      .pipe(catchError(e => { this.errorService.handleError(e); throw e; }));
  }

  previewProjectInvoice(body: { projectId: string; periodStart: string; periodEnd: string }): Observable<HttpResponse<InvoicePreviewResponse>> {
    return this.http
      .post<InvoicePreviewResponse>(`${environment.API_URL}/invoices/preview/project`, body, { observe: 'response' })
      .pipe(catchError(e => { this.errorService.handleError(e); throw e; }));
  }

  generateProjectInvoice(body: { projectId: string; periodStart?: string; periodEnd?: string; kind?: 'period' | 'adhoc'; invoiceDate?: string; metricSelection?: InvoiceMetricSelection[] }): Observable<HttpResponse<Invoice>> {
    return this.http
      .post<Invoice>(`${environment.API_URL}/invoices/generate/project`, body, { observe: 'response' })
      .pipe(catchError(e => { this.errorService.handleError(e); throw e; }));
  }

  generateDistributorInvoice(body: { companyId: string; periodStart: string; periodEnd: string; invoiceDate?: string }): Observable<HttpResponse<Invoice>> {
    return this.http
      .post<Invoice>(`${environment.API_URL}/invoices/generate/distributor`, body, { observe: 'response' })
      .pipe(catchError(e => { this.errorService.handleError(e); throw e; }));
  }

  confirm(id: string): Observable<HttpResponse<Invoice>> {
    return this.http
      .post<Invoice>(`${environment.API_URL}/invoices/${id}/confirm`, {}, { observe: 'response' })
      .pipe(catchError(e => { this.errorService.handleError(e); throw e; }));
  }

  finalize(id: string): Observable<HttpResponse<Invoice>> {
    return this.http
      .post<Invoice>(`${environment.API_URL}/invoices/${id}/finalize`, {}, { observe: 'response' })
      .pipe(catchError(e => { this.errorService.handleError(e); throw e; }));
  }

  revert(id: string): Observable<HttpResponse<Invoice>> {
    return this.http
      .post<Invoice>(`${environment.API_URL}/invoices/${id}/revert`, {}, { observe: 'response' })
      .pipe(catchError(e => { this.errorService.handleError(e); throw e; }));
  }

  restore(id: string): Observable<HttpResponse<Invoice>> {
    return this.http
      .post<Invoice>(`${environment.API_URL}/invoices/${id}/restore`, {}, { observe: 'response' })
      .pipe(catchError(e => { this.errorService.handleError(e); throw e; }));
  }

  addLineItem(id: string, lineItem: AddLineItemBody): Observable<HttpResponse<Invoice>> {
    return this.http
      .post<Invoice>(`${environment.API_URL}/invoices/${id}/line-items`, lineItem, { observe: 'response' })
      .pipe(catchError(e => { this.errorService.handleError(e); throw e; }));
  }

  updateLineItem(id: string, index: number, body: UpdateLineItemBody): Observable<HttpResponse<Invoice>> {
    return this.http
      .patch<Invoice>(`${environment.API_URL}/invoices/${id}/line-items/${index}`, body, { observe: 'response' })
      .pipe(catchError(e => { this.errorService.handleError(e); throw e; }));
  }

  removeLineItem(id: string, index: number): Observable<HttpResponse<Invoice>> {
    return this.http
      .delete<Invoice>(`${environment.API_URL}/invoices/${id}/line-items/${index}`, { observe: 'response' })
      .pipe(catchError(e => { this.errorService.handleError(e); throw e; }));
  }

  updateMetadata(id: string, body: Partial<InvoiceBillingSnapshot>): Observable<HttpResponse<Invoice>> {
    return this.http
      .patch<Invoice>(`${environment.API_URL}/invoices/${id}/metadata`, body, { observe: 'response' })
      .pipe(catchError(e => { this.errorService.handleError(e); throw e; }));
  }

  updateDiscounts(id: string, body: UpdateDiscountsBody): Observable<HttpResponse<Invoice>> {
    return this.http
      .patch<Invoice>(`${environment.API_URL}/invoices/${id}/discounts`, body, { observe: 'response' })
      .pipe(catchError(e => { this.errorService.handleError(e); throw e; }));
  }

  update(id: string, body: Partial<Invoice>): Observable<HttpResponse<Invoice>> {
    return this.http
      .patch<Invoice>(`${environment.API_URL}/invoices/${id}`, body, { observe: 'response' })
      .pipe(catchError(e => { this.errorService.handleError(e); throw e; }));
  }

  delete(id: string): Observable<HttpResponse<any>> {
    return this.http
      .delete(`${environment.API_URL}/invoices/${id}`, { observe: 'response' })
      .pipe(catchError(e => { this.errorService.handleError(e); throw e; }));
  }

  getPdfBlob(id: string): Observable<Blob> {
    return this.http
      .post(`${environment.API_URL}/invoices/${id}/pdf`, {}, { responseType: 'blob' })
      .pipe(catchError(e => { this.errorService.handleError(e); throw e; }));
  }

  sendByEmail(id: string, recipients: string[]): Observable<HttpResponse<{ sent: boolean }>> {
    return this.http
      .post<{ sent: boolean }>(`${environment.API_URL}/invoices/${id}/send`, { recipients }, { observe: 'response' })
      .pipe(catchError(e => { this.errorService.handleError(e); throw e; }));
  }
}
